import { graphql, useLazyLoadQuery, useSubscription } from 'react-relay';
import { useMemo, useState, useEffect } from 'react';
import { Box, GridItem, SimpleGrid, useTheme } from '@vitvio/design-system';
import { ORConsoleDetailHeader } from './ORConsoleDetailHeader';
import type { ORConsoleDetailQuery } from './__generated__/ORConsoleDetailQuery.graphql';
import { styledScrollBarProps } from '@vitvio/shared-components';
import { EventPanel } from '@vitvio/events';
import { TimerContentPanel } from './timer-content-panel';
import { getDifference } from '@vitvio/helpers';

const query = graphql`
  query ORConsoleDetailQuery($surgeryId: ID!) {
    surgery(id: $surgeryId) {
      id
      name
      plannedStart
      plannedStop
      status
      scheduleStatus
      actualStartTime
      actualStopTime
      estimatedStopTime
      location {
        name
      }
      ...TimerContentPanelFragment
      ...EventPanel
      ...ORConsoleDetailHeaderFragment
      turnover {
        ...TimerContentPanelFragment
        ...EventPanel
      }
    }
  }
`;

const subscription = graphql`
  subscription ORConsoleDetailSubscription($surgeryId: ID!) {
    surgeryUpdate(surgeryId: $surgeryId) {
      node {
        id
        name
        plannedStart
        plannedStop
        status
        scheduleStatus
        actualStartTime
        actualStopTime
        estimatedStopTime
        location {
          name
        }
        ...TimerContentPanelFragment
        ...EventPanel
        ...ORConsoleDetailHeaderFragment
        turnover {
          ...TimerContentPanelFragment
          ...EventPanel
        }
      }
    }
  }
`;

interface ORConsoleDetailProps {
  surgeryId: string;
}

const REFETCH_INTERVAL_SECONDS = 60;

export function ORConsoleDetail({ surgeryId }: ORConsoleDetailProps) {
  const { getSpacing } = useTheme();
  const [reloadKey, setReloadKey] = useState(0);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(new Date());

  const queryReference = useLazyLoadQuery<ORConsoleDetailQuery>(
    query,
    { surgeryId },
    { fetchPolicy: 'store-and-network', fetchKey: reloadKey },
  );

  const subscriptionConfig = useMemo(
    () => ({
      subscription,
      variables: {
        surgeryId,
      },
      onNext: () => {
        setLastUpdated(new Date());
      },
    }),
    [surgeryId],
  );

  useSubscription(subscriptionConfig);

  useEffect(() => {
    const checkForUpdates = () => {
      if (!lastUpdated) return;

      const now = new Date();
      const secondsSinceLastUpdate = getDifference(lastUpdated, now, 'seconds');

      if (secondsSinceLastUpdate >= REFETCH_INTERVAL_SECONDS) {
        setReloadKey((prev) => prev + 1);
      }
    };

    const interval = setInterval(checkForUpdates, 10000);
    return () => clearInterval(interval);
  }, [lastUpdated]);

  if (!queryReference) {
    return <div>Loading...</div>;
  }

  return (
    <Box display={'flex'} flexDirection={'column'} maxH={'100%'} h={'100%'}>
      <ORConsoleDetailHeader detailHeaderFragmentRef={queryReference.surgery || undefined} />

      <SimpleGrid
        flex={1}
        my={getSpacing('4')}
        p={getSpacing('5')}
        columns={4}
        columnGap={getSpacing('5')}
        overflowY={'auto'}
        sx={styledScrollBarProps}
      >
        <GridItem colSpan={1} overflow={'hidden'} minW={'300px'} maxW={'400px'}>
          <EventPanel
            surgeryDataRef={queryReference.surgery || undefined}
            turnoverDataRef={queryReference.surgery?.turnover || undefined}
          />
        </GridItem>
        <GridItem colSpan={3}>
          {queryReference.surgery && <TimerContentPanel fragmentRef={queryReference.surgery} category="SURGERY" />}

          {queryReference.surgery?.turnover && (
            <TimerContentPanel fragmentRef={queryReference.surgery.turnover} category="TURNOVER" />
          )}
        </GridItem>
      </SimpleGrid>
    </Box>
  );
}
